import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  FiHome,
  FiFolder,
  FiUser,
  FiSettings,
  FiChevronDown,
  FiChevronUp,
  FiAlertCircle,
  FiShield,
  FiChevronsLeft,
  FiChevronsRight,
  FiX
} from 'react-icons/fi';
import { isAdmin } from '../utils/auth';
import './Sidebar.css';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  // Mobile off-canvas drawer state — independent of the desktop
  // collapsed/expanded rail. Toggled by Navbar's hamburger button via a
  // custom event (matches this app's existing cross-component pattern,
  // e.g. 'departmentChanged'/'projectChanged'), since Navbar and Sidebar
  // are unrelated siblings under App.js with no shared state/context.
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onToggle = () => setMobileOpen((prev) => !prev);
    globalThis.addEventListener('toggleMobileSidebar', onToggle);
    return () => globalThis.removeEventListener('toggleMobileSidebar', onToggle);
  }, []);

  // Close the mobile drawer whenever the route changes (i.e. after navigating).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Menu items configuration
  const menuItems = useMemo(() => [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: FiHome,
      path: '/dashboard',
      badge: null,
      requiresAdmin: false
    },
    {
      id: 'projects',
      label: 'Select Dashboard',
      icon: FiFolder,
      path: '/projects',
      badge: null,
      requiresAdmin: true
    },
    {
      id: 'admin-panel',
      label: 'Admin Panel',
      icon: FiShield,
      path: '/admin',
      badge: null,
      requiresAdmin: true
    },
    {
      id: 'help',
      label: 'Help & Support',
      icon: FiAlertCircle,
      path: '/help',
      badge: null,
      requiresAdmin: false
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: FiUser,
      path: '/profile',
      badge: null,
      requiresAdmin: false
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: FiSettings,
      path: '/settings',
      badge: null,
      requiresAdmin: false
    }
  ], []);

  /**
   * Filter menu items based on user role
   */
  const visibleMenuItems = useMemo(() => menuItems.filter(item => {
    if (item.requiresAdmin) {
      return isAdmin();
    }
    return true;
  }), [menuItems]);

  /**
   * Determine active menu on route change
   */
  useEffect(() => {
    const currentPath = location.pathname;

    const matchedItem = visibleMenuItems.find((item) => {
      if (item.path === currentPath) return true;
      if (item.submenu) {
        return item.submenu.some((subitem) => subitem.path === currentPath);
      }
      return false;
    });

    if (matchedItem) {
      setActiveMenu(matchedItem.id);
    }
  }, [location.pathname, visibleMenuItems]);

  /**
   * Handle menu item click
   */
  const handleMenuClick = (item) => {
    // If item has submenu, only toggle it (don't navigate)
    if (item.submenu) {
      setActiveMenu(activeMenu === item.id ? null : item.id);
    } 
    // If no submenu but has path, navigate
    else if (item.path) {
      navigate(item.path);
      setActiveMenu(item.id);
    }
  };

  // submenu clicks are handled inside NavItem; removed unused handler

  // On the mobile off-canvas drawer, always show full labels regardless of
  // the desktop collapsed/expanded rail state — an icon-only overlay drawer
  // isn't useful once it's already taking a deliberate tap to open.
  const showLabels = expanded || mobileOpen;

  return (
    <>
      {mobileOpen && <div className="sidebar-mobile-backdrop" onClick={() => setMobileOpen(false)} />}
      <aside className={`sidebar ${expanded ? 'expanded' : 'collapsed'} ${mobileOpen ? 'mobile-open' : ''}`}>
      {/* Toggle Button (desktop collapse/expand) */}
      <div className="sidebar-toggle">
        <button
          className="toggle-btn"
          onClick={() => setExpanded(!expanded)}
          title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-label={expanded ? 'Collapse sidebar navigation' : 'Expand sidebar navigation'}
          aria-expanded={expanded}
        >
          {expanded ? <FiChevronsLeft size={16} /> : <FiChevronsRight size={16} />}
        </button>
      </div>

      {/* Close button (mobile drawer only) */}
      <button
        type="button"
        className="sidebar-mobile-close"
        onClick={() => setMobileOpen(false)}
        aria-label="Close menu"
      >
        <FiX size={18} />
      </button>

      {/* Sideabar Content */}
      <nav className="sidebar-nav">
        <div className="nav-section">
          <div className="section-label">Navigation</div>

          <ul className="nav-list">
            {visibleMenuItems.filter(item =>
              ['dashboard', 'projects', 'it-assets'].includes(item.id)
            ).map(item => (
              <li key={item.id}>
                <NavItem
                  item={item}
                  isActive={activeMenu === item.id || location.pathname === item.path}
                  isExpanded={showLabels}
                  onClick={() => handleMenuClick(item)}
                />
              </li>
            ))}
          </ul>
        </div>

        <div className="nav-section">
          <div className="section-label">Account</div>

          <ul className="nav-list">
            {visibleMenuItems.filter(item =>
              ['admin-panel', 'help', 'profile', 'settings'].includes(item.id)
            ).map(item => (
              <li key={item.id}>
                <NavItem
                  item={item}
                  isActive={activeMenu === item.id || location.pathname === item.path}
                  isExpanded={showLabels}
                  onClick={() => handleMenuClick(item)}
                />
              </li>
            ))}
          </ul>
        </div>

      </nav>
      </aside>
    </>
  );
};

/**
 * NavItem Component
 */
const NavItem = ({
  item,
  isActive,
  isExpanded,
  hasSubmenu,
  isSubmenuOpen,
  onClick
}) => {
  const Icon = item.icon;

  const NavElement = item.path ? 'a' : 'button';
  const navProps = item.path 
    ? { href: item.path, onClick: (e) => { e.preventDefault(); onClick(); } }
    : { onClick };

  return (
    <OverlayTrigger
      placement="right"
      overlay={!isExpanded ? <Tooltip>{item.label}</Tooltip> : <></>}
      show={!isExpanded ? undefined : false}
    >
      <NavElement
        className={`nav-item ${isActive ? 'active' : ''} ${item.requiresAdmin ? 'admin-item' : ''}`}
        {...navProps}
      >
        <div className="nav-icon">
          <Icon size={20} />
        </div>

        {isExpanded && (
          <>
            <span className="nav-label">{item.label}</span>

            {item.badge && (
              <span className="nav-badge">{item.badge}</span>
            )}

            {hasSubmenu && (
              <span className="nav-chevron">
                {isSubmenuOpen ? (
                  <FiChevronUp size={16} />
                ) : (
                  <FiChevronDown size={16} />
                )}
              </span>
            )}
          </>
        )}
      </NavElement>
    </OverlayTrigger>
  );
};

export default Sidebar;
